import { useEffect, useState } from 'react';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useTimeout } from '@shared/hooks/useTimeout';
import { usePrefersReducedMotion } from '@shared/hooks/usePrefersReducedMotion';

export const CYCLING_LABEL_INTERVAL_MS = 3000;

/**
 * Rotates through `items` on a timer while `enabled`, so an idle pill can advertise its
 * options ("Feature", then "Free", then "Photo", and so on; Jeff, 2026-10-08). Returns
 * `undefined` when there is nothing to show, so the caller falls back to its static label.
 * Starts at the first item (deterministic for SSR) and resets when the items change. After
 * `maxTrips` full passes it comes to rest back on the first item and the timer stops.
 *
 * Auto-updating text has to be stoppable (WCAG 2.2.2), and the two ways of stopping it differ:
 *
 * - `isPaused` **freezes** the rotation on the item currently showing. The caller sets it while
 *   the pointer or focus is on the pill, so the label someone is reading holds still instead of
 *   swapping — or snapping back to the static label, which would move under them just as much.
 * - `prefers-reduced-motion` stands the feature **down**: no timer, and `undefined`, so the pill
 *   shows its own static label. Holding on one option instead would read as a selection.
 *
 * Nothing cycles until after mount. The server cannot know the motion preference, so rendering
 * a cycled label there and the static one on a reduced-motion client is a hydration text
 * mismatch; starting from the static label on both sides removes the question.
 */
export const useCyclingLabel = (
  items: ReadonlyArray<string>,
  enabled: boolean,
  isPaused = false,
  intervalMs = CYCLING_LABEL_INTERVAL_MS,
  maxTrips = Infinity,
): string | undefined => {
  const [index, setIndex] = useState(0);
  const isMounted = useIsMounted();
  const prefersReducedMotion = usePrefersReducedMotion();
  const { set: setStepTimeout, clear: clearStepTimeout } = useTimeout();
  const key = items.join('|');

  // Reset during render rather than in an effect: an effect would paint the new items at the
  // old index first and then immediately re-render.
  const [renderedKey, setRenderedKey] = useState(key);
  if (key !== renderedKey) {
    setRenderedKey(key);
    setIndex(0);
  }

  // `index` counts every step rather than wrapping, so it also records how many trips are done.
  const isFinished = index >= maxTrips * items.length;

  // One timeout per step, re-armed after each label renders, rather than a `setInterval`. An
  // interval runs on a fixed schedule: when hydration blocks the main thread past a tick, that
  // tick fires late but the next still fires on time, so the first labels on page load flashed
  // by in a fraction of the interval. Re-arming from the render gives every label its full time.
  useEffect(() => {
    if (!enabled || !isMounted || prefersReducedMotion || isPaused || isFinished || items.length < 2) return;
    setStepTimeout(() => setIndex(current => current + 1), intervalMs);
    return clearStepTimeout;
  }, [enabled, isMounted, prefersReducedMotion, isPaused, isFinished, items.length, intervalMs, key, index, setStepTimeout, clearStepTimeout]);

  if (!enabled || !isMounted || prefersReducedMotion || items.length === 0) return undefined;
  return items[index % items.length];
};
